"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  CheckboxRow,
  ResultBanner,
  PrintButton,
  CopyButton,
  PrintFooter,
  DataReviewedNote,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Construction welfare facilities — CDM 2015 Schedule 2 (the legal duties) +
   BS 6465-1:2006 (the commonly applied numeric scale).

   CDM 2015 Schedule 2 sets the duties QUALITATIVELY ("suitable and sufficient"
   sanitary conveniences, washing facilities, drinking water, changing rooms /
   lockers, and rest rooms). It does not give numbers. HSE construction welfare
   guidance CIS59 points to BS 6465-1:2006, which recommends a ratio of
   1 toilet to 7 persons where portable toilets are emptied once a week.
   Verified 6 June 2026 against hse.gov.uk/pubns/cis59.pdf and
   legislation.gov.uk/uksi/2015/51 (Schedule 2).

   Numeric scale used here (BS 6465-1 benchmark — NOT law):
   - Water-flushed toilets, mixed / women: 1 toilet + 1 washbasin per 7 people.
   - Men-only with urinals: WCs scale more slowly because urinals share the load
     (approx 1 WC + 1 urinal per 15 men, plus a washbasin per ~10), but the
     simple, defensible site rule remains roughly 1 toilet to 7.
   - Chemical / non-flushing toilets emptied less often need a more generous
     ratio — provide extra units and more frequent servicing.

   The Schedule 2 checklist below is the legal baseline that applies on EVERY
   site regardless of size or duration. */

type Gender = "mixed" | "split";
type Duration = "short" | "medium" | "long";
type ToiletType = "flush" | "chemical";

interface Counts {
  toilets: number;
  washbasins: number;
  urinals: number; // men-only provision, informational
}

const PEOPLE_PER_TOILET_FLUSH = 7; // BS 6465-1:2006 (HSE CIS59) — 1:7, emptied weekly
const PEOPLE_PER_TOILET_CHEMICAL = 5; // more generous where not water-flushed / serviced less often

/** Toilets + washbasins for a population, flush vs chemical. */
function provisionFor(people: number, toiletType: ToiletType): { toilets: number; washbasins: number } {
  const per = toiletType === "flush" ? PEOPLE_PER_TOILET_FLUSH : PEOPLE_PER_TOILET_CHEMICAL;
  const toilets = Math.max(1, Math.ceil(people / per));
  // A washbasin must sit alongside every toilet (Schedule 2 para 2), so basins
  // track toilet numbers as a minimum.
  const washbasins = toilets;
  return { toilets, washbasins };
}

interface Outcome {
  level: Verdict;
  title: string;
  counts: Counts;
  /** Per-sex breakdown when men/women split is known. */
  breakdown: { label: string; toilets: number; washbasins: number; urinals?: number }[];
  notes: string[];
}

function buildOutcome(
  people: number,
  gender: Gender,
  men: number,
  women: number,
  mainsWater: boolean,
  duration: Duration,
  toiletType: ToiletType,
): Outcome {
  const notes: string[] = [];
  const breakdown: Outcome["breakdown"] = [];
  let totalToilets = 0;
  let totalBasins = 0;
  let totalUrinals = 0;

  if (gender === "split" && men + women > 0) {
    // Women / mixed-style: 1 toilet per 7. Men: urinals share load so WCs
    // scale more slowly, but keep a washbasin alongside.
    const womenProv = provisionFor(women, toiletType);
    breakdown.push({ label: "Women", toilets: womenProv.toilets, washbasins: womenProv.washbasins });
    totalToilets += womenProv.toilets;
    totalBasins += womenProv.washbasins;

    // Men: provide WCs at roughly 1 per 15 (urinals carry the rest), urinals at
    // ~1 per 15, washbasins at ~1 per 10 — but never fewer than one of each.
    const menWcs = men > 0 ? Math.max(1, Math.ceil(men / 15)) : 0;
    const menUrinals = men > 0 ? Math.max(1, Math.ceil(men / 15)) : 0;
    const menBasins = men > 0 ? Math.max(1, Math.ceil(men / 10)) : 0;
    if (men > 0) {
      breakdown.push({ label: "Men", toilets: menWcs, washbasins: menBasins, urinals: menUrinals });
      totalToilets += menWcs;
      totalBasins += menBasins;
      totalUrinals += menUrinals;
    }
    notes.push(
      "Separate provision shown for men and women. Schedule 2 requires separate rooms unless each toilet is in its own lockable room. Men's WC numbers are lower because urinals share the load; women's follow the 1-in-7 scale.",
    );
  } else {
    const prov = provisionFor(people, toiletType);
    totalToilets = prov.toilets;
    totalBasins = prov.washbasins;
    breakdown.push({ label: "Mixed / shared", toilets: prov.toilets, washbasins: prov.washbasins });
    notes.push(
      "Mixed-use provision: men and women may share toilets only where each is in a separate lockable room partitioned from any urinals — otherwise provide separate facilities.",
    );
  }

  if (toiletType === "chemical") {
    notes.push(
      "Chemical / portable non-flushing toilets are acceptable only where connection to mains drainage or a holding tank is not reasonably practicable. Provide extra units and more frequent servicing, and washing facilities (clean hot and cold or warm water) right beside them.",
    );
  } else if (!mainsWater) {
    notes.push(
      "No mains water: you must still provide running hot and cold (or warm) water for washing so far as reasonably practicable — use a bowser, water heater or self-contained welfare unit. Flushing toilets need a connected supply or a holding tank.",
    );
  }

  if (duration === "long") {
    notes.push(
      "Longer-duration works: install permanent or semi-permanent welfare connected to mains water and drainage where reasonably practicable, rather than relying on basic portable units.",
    );
  } else if (duration === "short") {
    notes.push(
      "Short-duration / transient work: a mobile or self-contained welfare unit (or, only where genuinely impractical, agreed access to nearby public facilities) can meet the duty — but the facilities must still be available before work starts.",
    );
  }

  const counts: Counts = { toilets: totalToilets, washbasins: totalBasins, urinals: totalUrinals };

  const level: Verdict = "low";
  const title = `${counts.toilets} toilet${counts.toilets > 1 ? "s" : ""} and ${counts.washbasins} washbasin${counts.washbasins > 1 ? "s" : ""} for ${people} ${people === 1 ? "person" : "people"}`;

  return { level, title, counts, breakdown, notes };
}

/* CDM 2015 Schedule 2 fixed requirements — the qualitative legal baseline that
   applies on every site. */
const SCHEDULE2_CHECKLIST = [
  "Sanitary conveniences (toilets) — suitable, sufficient, ventilated, lit and kept clean; separate for men and women unless each is in a lockable room.",
  "Washing facilities beside every toilet and any changing room — with clean hot and cold (or warm) running water so far as reasonably practicable.",
  "Soap or other means of cleaning, and towels or other means of drying.",
  "Showers where the nature of the work or health reasons require them (e.g. hazardous substances, very dirty work).",
  "An adequate supply of wholesome drinking water at readily accessible places, marked where necessary, with cups unless it is a drinking jet.",
  "Changing rooms with seating, and means to dry special clothing, where workers must wear special clothing and cannot change elsewhere.",
  "Lockers / secure storage for special clothing not taken home, personal clothing and personal effects, where needed.",
  "Rest rooms or rest areas with enough tables and seating, a means to heat food and to boil water, and protection from the weather.",
];

const GENDER_LABEL: Record<Gender, string> = {
  mixed: "Mixed / not specified",
  split: "Known men / women split",
};
const DURATION_LABEL: Record<Duration, string> = {
  short: "Short / transient (days)",
  medium: "Medium (weeks to a few months)",
  long: "Long (many months or more)",
};

export function WelfareFacilitiesCalculator() {
  const [peopleStr, setPeopleStr] = useState("");
  const [gender, setGender] = useState<Gender>("mixed");
  const [menStr, setMenStr] = useState("");
  const [womenStr, setWomenStr] = useState("");
  const [mainsWater, setMainsWater] = useState(true);
  const [duration, setDuration] = useState<Duration>("medium");
  const [toiletType, setToiletType] = useState<ToiletType>("flush");
  const [siteName, setSiteName] = useState("");
  const resultTracked = useRef(false);

  const men = useMemo(() => {
    const n = parseInt(menStr, 10);
    return isFinite(n) && n > 0 ? n : 0;
  }, [menStr]);
  const women = useMemo(() => {
    const n = parseInt(womenStr, 10);
    return isFinite(n) && n > 0 ? n : 0;
  }, [womenStr]);

  const people = useMemo(() => {
    if (gender === "split") {
      const sum = men + women;
      return sum > 0 ? sum : null;
    }
    const n = parseInt(peopleStr, 10);
    return isFinite(n) && n > 0 ? n : null;
  }, [gender, men, women, peopleStr]);

  const outcome = useMemo(() => {
    if (people === null) return null;
    return buildOutcome(people, gender, men, women, mainsWater, duration, toiletType);
  }, [people, gender, men, women, mainsWater, duration, toiletType]);

  useEffect(() => {
    if (outcome && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("welfare-facilities-calculator", {
        people,
        toilets: outcome.counts.toilets,
      });
    }
  }, [outcome, people]);

  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const buildCopyText = () => {
    if (!outcome) return "";
    const lines: string[] = [];
    lines.push("WELFARE PROVISION RECORD");
    if (siteName) lines.push(`Site: ${siteName}`);
    lines.push(`Date: ${today}`);
    lines.push("");
    lines.push(`Max people on site: ${people}`);
    if (gender === "split") lines.push(`Men: ${men}  Women: ${women}`);
    lines.push(`Toilet type: ${toiletType === "flush" ? "Water-flushed" : "Chemical / portable"}`);
    lines.push(`Mains water: ${mainsWater ? "Yes" : "No"}`);
    lines.push(`Duration: ${DURATION_LABEL[duration]}`);
    lines.push("");
    lines.push("SUGGESTED NUMBERS (BS 6465-1 benchmark — not a legal scale):");
    outcome.breakdown.forEach((b) => {
      lines.push(
        `- ${b.label}: ${b.toilets} toilet(s), ${b.washbasins} washbasin(s)${b.urinals ? `, ${b.urinals} urinal(s)` : ""}`,
      );
    });
    lines.push(`- Total: ${outcome.counts.toilets} toilet(s), ${outcome.counts.washbasins} washbasin(s)`);
    lines.push("");
    lines.push("CDM 2015 SCHEDULE 2 — REQUIRED ON EVERY SITE:");
    SCHEDULE2_CHECKLIST.forEach((c) => lines.push(`- [ ] ${c}`));
    if (outcome.notes.length) {
      lines.push("");
      lines.push("NOTES FOR THIS SITE:");
      outcome.notes.forEach((n) => lines.push(`- ${n}`));
    }
    lines.push("");
    lines.push("CDM 2015 Schedule 2 sets the duties in qualitative terms; the numbers are the commonly applied BS 6465-1 benchmark.");
    lines.push("Welfare must be in place before work starts and maintained throughout.");
    lines.push("Generated with the free welfare facilities calculator at ramsdocs.co.uk");
    return lines.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Your site</h2>
        <p className="mt-1 text-sm text-muted">
          CDM 2015 sets the welfare duties in words, not numbers. This sizes toilets and washbasins against the
          commonly used BS 6465-1 scale and lists the fixed Schedule 2 requirements.
        </p>

        <div className="mt-5 space-y-5">
          <Field label="Headcount" hint="The most people on site at any one time, including subcontractors.">
            {gender === "split" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Men">
                  <NumberInput min={0} step={1} value={menStr} onChange={(e) => setMenStr(e.target.value)} placeholder="e.g. 10" />
                </Field>
                <Field label="Women">
                  <NumberInput min={0} step={1} value={womenStr} onChange={(e) => setWomenStr(e.target.value)} placeholder="e.g. 4" />
                </Field>
              </div>
            ) : (
              <NumberInput
                min={1}
                step={1}
                value={peopleStr}
                onChange={(e) => setPeopleStr(e.target.value)}
                placeholder="e.g. 14"
              />
            )}
          </Field>

          <Field label="Workforce split" hint="A known men/women split changes toilet provision (urinals share the men's load).">
            <SelectInput value={gender} onChange={(e) => setGender(e.target.value as Gender)}>
              <option value="mixed">{GENDER_LABEL.mixed}</option>
              <option value="split">{GENDER_LABEL.split}</option>
            </SelectInput>
          </Field>

          <Field label="Toilet type">
            <SelectInput value={toiletType} onChange={(e) => setToiletType(e.target.value as ToiletType)}>
              <option value="flush">Water-flushed (mains or holding tank)</option>
              <option value="chemical">Chemical / portable non-flushing</option>
            </SelectInput>
          </Field>

          <Field label="Duration of works">
            <SelectInput value={duration} onChange={(e) => setDuration(e.target.value as Duration)}>
              <option value="short">{DURATION_LABEL.short}</option>
              <option value="medium">{DURATION_LABEL.medium}</option>
              <option value="long">{DURATION_LABEL.long}</option>
            </SelectInput>
          </Field>

          <Field label="Water supply">
            <CheckboxRow checked={mainsWater} onChange={setMainsWater}>
              Mains water is available on site.
            </CheckboxRow>
          </Field>

          <Field label="" className="min-w-[200px]">
            <TextInput
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="Site name (for the printed record)"
            />
          </Field>
        </div>

        {!outcome && (
          <p className="mt-5 rounded-lg bg-mist px-4 py-3 text-sm text-muted">
            Enter the headcount to see the suggested welfare provision.
          </p>
        )}
      </div>

      {/* Result + printable welfare record */}
      <div className="space-y-4">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="welfare-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Welfare provision record
              </div>
              <h2 className="mt-1 text-lg">
                {siteName ? `${siteName} — ` : ""}
                {today}
              </h2>

              <div className="mt-4">
                <ResultBanner level={outcome.level} title={outcome.title}>
                  Suggested numbers using the BS 6465-1 benchmark (1 water-flushed toilet and washbasin per 7
                  people). These numbers are not a legal scale — CDM 2015 Schedule 2 sets the duties; you provide
                  what is suitable and sufficient.
                </ResultBanner>
              </div>

              <h3 className="mt-5 text-[0.95rem] font-semibold text-ink">Toilets &amp; washbasins (BS 6465-1)</h3>
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
                    <th className="py-1.5 pr-2 font-semibold">For</th>
                    <th className="py-1.5 pr-2 text-right font-semibold">Toilets</th>
                    <th className="py-1.5 pr-2 text-right font-semibold">Washbasins</th>
                    <th className="py-1.5 text-right font-semibold">Urinals</th>
                  </tr>
                </thead>
                <tbody>
                  {outcome.breakdown.map((b) => (
                    <tr key={b.label} className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">{b.label}</td>
                      <td className="py-2 pr-2 text-right font-semibold text-ink">{b.toilets}</td>
                      <td className="py-2 pr-2 text-right font-semibold text-ink">{b.washbasins}</td>
                      <td className="py-2 text-right text-ink">{b.urinals ?? "—"}</td>
                    </tr>
                  ))}
                  {outcome.breakdown.length > 1 && (
                    <tr>
                      <td className="py-2 pr-2 font-semibold text-ink">Total</td>
                      <td className="py-2 pr-2 text-right font-display text-lg font-bold text-emerald-deep">
                        {outcome.counts.toilets}
                      </td>
                      <td className="py-2 pr-2 text-right font-display text-lg font-bold text-emerald-deep">
                        {outcome.counts.washbasins}
                      </td>
                      <td className="py-2 text-right text-ink">{outcome.counts.urinals || "—"}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <h3 className="mt-5 text-[0.95rem] font-semibold text-ink">
                CDM 2015 Schedule 2 — required on every site
              </h3>
              <p className="mt-1 text-[0.8rem] text-muted">
                These duties apply regardless of size or duration, and must be in place before work starts.
              </p>
              <ul className="mt-3 space-y-2">
                {SCHEDULE2_CHECKLIST.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-[0.88rem] leading-relaxed text-body">
                    <span className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded border border-ink/40" aria-hidden />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>

              {outcome.notes.length > 0 && (
                <>
                  <h3 className="mt-5 text-[0.95rem] font-semibold text-ink">Notes for this site</h3>
                  <ul className="mt-3 space-y-2.5">
                    {outcome.notes.map((n, i) => (
                      <li key={i} className="flex gap-2.5 text-[0.88rem] leading-relaxed text-body">
                        <svg
                          viewBox="0 0 24 24"
                          className="mt-0.5 h-4 w-4 shrink-0 text-emerald-deep"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="mt-5 rounded-lg border border-emerald/30 bg-emerald-tint px-3 py-2 text-[0.82rem] leading-relaxed text-emerald-deep">
                <strong>Duties vs numbers.</strong> CDM 2015 Schedule 2 sets the welfare duties in qualitative
                terms (&quot;suitable and sufficient&quot;). The toilet and washbasin figures here are the
                commonly applied BS 6465-1 benchmark, not a legal requirement — provide more where the work,
                conditions or servicing interval call for it.
              </p>

              <DataReviewedNote date="6 June 2026">
                Duties from CDM 2015 Schedule 2; numeric scale from BS 6465-1:2006 as cited in HSE guidance.
                Check the current sources before relying on the result.
              </DataReviewedNote>

              <PrintFooter toolName="welfare facilities calculator" />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton tool="welfare-facilities-calculator" buildText={buildCopyText} label="Copy record" />
              <PrintButton tool="welfare-facilities-calculator" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Enter your site headcount to see the suggested toilets and washbasins, plus the CDM 2015 Schedule 2
            welfare checklist that applies to every construction site.
          </div>
        )}
      </div>
    </div>
  );
}
